import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

export interface SEOProps {
    title?: string;
    description?: string;
    keywords?: string[];
    image?: string;
    url?: string;
    type?: 'website' | 'article' | 'product';
    author?: string;
    publishedTime?: string;
    modifiedTime?: string;
    noindex?: boolean;
    nofollow?: boolean;
    canonicalUrl?: string;
    structuredData?: any;
}

const DEFAULT_SEO = {
    title: 'HyVay - Build your app easily',
    description: 'Platform No-Code App Builder untuk membuat aplikasi dengan mudah dan cepat. Bikin Aplikasi, Sat-Set Jadi!',
    keywords: [
        'no-code',
        'app builder',
        'website builder',
        'aplikasi',
        'indonesia',
        'platform',
        'low-code',
        'pembuat aplikasi',
        'buat website',
        'tanpa coding',
    ],
    image: 'https://hyvay.com/images/logo.png',
    url: 'https://hyvay.com',
    type: 'website' as const,
};

export const SEOHead: React.FC<SEOProps> = ({
    title,
    description,
    keywords,
    image,
    url,
    type = 'website',
    author,
    publishedTime,
    modifiedTime,
    noindex = false,
    nofollow = false,
    canonicalUrl,
    structuredData,
}) => {
    const seoTitle = title ? `${title} | HyVay` : DEFAULT_SEO.title;
    const seoDescription = description || DEFAULT_SEO.description;
    const seoKeywords = keywords || DEFAULT_SEO.keywords;
    const seoImage = image || DEFAULT_SEO.image;
    const seoUrl = url || DEFAULT_SEO.url;

    const robotsContent = [
        noindex ? 'noindex' : 'index',
        nofollow ? 'nofollow' : 'follow',
    ].join(', ');

    useEffect(() => {
        // Inject structured data if provided
        if (structuredData) {
            const scriptId = 'structured-data-seo';
            let script = document.getElementById(scriptId) as HTMLScriptElement;

            if (!script) {
                script = document.createElement('script');
                script.id = scriptId;
                script.type = 'application/ld+json';
                document.head.appendChild(script);
            }

            script.textContent = JSON.stringify(structuredData);
        }
    }, [structuredData]);

    return (
        <Helmet>
            {/* Basic Meta Tags */}
            <title>{seoTitle}</title>
            <meta name="description" content={seoDescription} />
            <meta name="keywords" content={seoKeywords.join(', ')} />
            <meta name="robots" content={robotsContent} />
            {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

            {/* Language and Locale */}
            <html lang="id" />
            <meta property="og:locale" content="id_ID" />

            {/* Open Graph Tags */}
            <meta property="og:type" content={type} />
            <meta property="og:title" content={seoTitle} />
            <meta property="og:description" content={seoDescription} />
            <meta property="og:image" content={seoImage} />
            <meta property="og:url" content={seoUrl} />
            <meta property="og:site_name" content="HyVay" />

            {/* Article specific tags */}
            {type === 'article' && author && (
                <meta property="article:author" content={author} />
            )}
            {type === 'article' && publishedTime && (
                <meta property="article:published_time" content={publishedTime} />
            )}
            {type === 'article' && modifiedTime && (
                <meta property="article:modified_time" content={modifiedTime} />
            )}

            {/* Twitter Card Tags */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={seoTitle} />
            <meta name="twitter:description" content={seoDescription} />
            <meta name="twitter:image" content={seoImage} />
            {/* Add your Twitter handle here if you have one */}
            {/* <meta name="twitter:site" content="@hyvay" /> */}

            {/* Additional Meta Tags */}
            <meta name="theme-color" content="#6366f1" />
            <meta name="apple-mobile-web-app-capable" content="yes" />
            <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
        </Helmet>
    );
};
